import { observer } from 'mobx-react-lite';
import { timestripRegionName } from 'owa-locstrings/lib/strings/timestripregionname.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import dateRangeToDisplayString from '../utils/dateRangeToDisplayString';

import { ActionButton } from '@fluentui/react/lib/Button';
import { Callout } from '@fluentui/react/lib/Callout';
import { ControlIcons } from 'owa-control-icons';
import { createDateRangeFromType, DateRange } from 'owa-datetime-utils';
import { DateRangeType, DayOfWeek } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import type { OwaDate } from 'owa-datetime';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import { InlineCalendar } from 'owa-datepicker';
import { Icon } from '@fluentui/react/lib/Icon';

const styles = require('./Timestrip.scss');
const classNames = require('classnames/bind').bind(styles);

/**
 * The timestrip properties
 */
export interface TimestripProps extends React.HTMLProps<HTMLDivElement> {
    dateRange: DateRange;
    dateRangeType: DateRangeType;
    workDays: DayOfWeek[];
    weekStartDay: number;
    showWeekNumbers: boolean;
    firstWeekOfYear: any;
    useWeekDay: boolean;

    selectionClassName?: string;
    buttonLabelClassName?: string;
    menuIconClassName?: string;

    // Callback issued when a new date range is selected
    onDateRangeChange: (dateRange: DateRange) => void;

    // Optional setting to apply selection styling
    applySelectionStyling?: boolean;

    // Optional override for date display string
    customDateDisplayString?: string;

    // Optional callback that will be invoked whenever a date is selected; use for additional functionality such as logging usage
    onDayPickerDateSelected?: () => void;

    // Optional override for showing month picker as overlay
    showMonthPickerAsOverlay?: boolean;

    numDaysInDayRange?: number;
    ariaRole?: string;
}

/**
 * The timestrip's state
 */
export interface TimestripState {
    dateRange?: DateRange;
    isTimestripCalloutVisible?: boolean;
}

/**
 * A timestrip class with an integrated callout for changing the selected dates and displaying either month or day picker views
 */
export default observer(function Timestrip(props: TimestripProps) {
    const dateRangeElement = React.useRef<HTMLDivElement>();
    const [isTimestripCalloutVisible, setIsTimestripCalloutVisible] = React.useState<boolean>(
        false
    );
    const onDateRangeClicked = () => {
        setIsTimestripCalloutVisible(!isTimestripCalloutVisible);
    };
    const onCalloutDismiss = () => {
        setIsTimestripCalloutVisible(false);
    };
    const onDateRangeChanged = (newDateRange: DateRange) => {
        let { onDateRangeChange } = props;
        if (onDateRangeChange) {
            onDateRangeChange(newDateRange);
        }
    };
    const onDayPickerDateSelected = (selectedDate: OwaDate) => {
        let newDateRange: DateRange;
        let {
            dateRangeType,
            weekStartDay,
            workDays,
            onDayPickerDateSelected,
            numDaysInDayRange,
        } = props;
        newDateRange = createDateRangeFromType(
            dateRangeType,
            selectedDate,
            weekStartDay,
            workDays,
            numDaysInDayRange
        );
        onCalloutDismiss();
        onDateRangeChanged(newDateRange);
        onDayPickerDateSelected?.();
    };
    const {
        applySelectionStyling,
        selectionClassName,
        buttonLabelClassName,
        dateRange,
        dateRangeType,
        weekStartDay,
        showWeekNumbers,
        firstWeekOfYear,
        workDays,
        useWeekDay,
        customDateDisplayString,
        showMonthPickerAsOverlay,
        numDaysInDayRange,
        ariaRole,
        menuIconClassName,
    } = props;
    const selectedDate = dateRange.start;
    const dateRangeClass = classNames(
        props.className,
        isTimestripCalloutVisible && applySelectionStyling && selectionClassName,
        applySelectionStyling && {
            dateRangeSelected: isTimestripCalloutVisible,
            dateRangeUnselected: !isTimestripCalloutVisible,
        }
    );
    const displayString = customDateDisplayString
        ? customDateDisplayString
        : dateRangeToDisplayString(
              dateRange,
              dateRangeType,
              weekStartDay,
              showWeekNumbers,
              firstWeekOfYear,
              useWeekDay,
              numDaysInDayRange
          );

    return (
        <>
            <ActionButton
                className={dateRangeClass}
                title={loc(timestripRegionName)}
                onClick={onDateRangeClicked}
                aria-posinset={props['aria-posinset']}
                aria-setsize={props['aria-setsize']}
                role={ariaRole}>
                <div
                    ref={dateRangeElement}
                    className={classNames(buttonLabelClassName, styles.buttonLabel)}>
                    <div className={classNames(styles.buttonLabelText, buttonLabelClassName)}>
                        {displayString}
                    </div>
                    <Icon className={menuIconClassName} iconName={ControlIcons.ChevronDown} />
                </div>
            </ActionButton>
            {isTimestripCalloutVisible && (
                <Callout
                    isBeakVisible={false}
                    gapSpace={10}
                    target={dateRangeElement.current}
                    onDismiss={onCalloutDismiss}
                    setInitialFocus={true}>
                    <FocusTrapZone isClickableOutsideFocusTrap={true}>
                        <InlineCalendar
                            value={selectedDate}
                            showMonthPickerAsOverlay={showMonthPickerAsOverlay ? true : false}
                            showGoToToday={dateRangeType !== DateRangeType.Month}
                            dateRangeType={dateRangeType}
                            onSelectDate={onDayPickerDateSelected}
                            isDayPickerVisible={dateRangeType !== DateRangeType.Month}
                            highlightCurrentMonth={false}
                            highlightSelectedMonth={true}
                            firstDayOfWeek={weekStartDay}
                            showWeekNumbers={showWeekNumbers}
                            firstWeekOfYear={firstWeekOfYear}
                            workWeekDays={workDays}
                            calendarDayProps={
                                numDaysInDayRange
                                    ? { daysToSelectInDayView: numDaysInDayRange }
                                    : null
                            }
                        />
                    </FocusTrapZone>
                </Callout>
            )}
        </>
    );
});
